import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SoecoButton } from "../../components/SoecoButton";
import {
    useDeleteCompany,
    useUpdateCompany,
    useGetCompany,
    useIsSubSubCompany,
} from "../../hooks/companyHooks";
import { ErrorRecord } from "../../types";
import styled from "styled-components";
import { SoecoHeader } from "../../components/SoecoHeader";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../../components/Modals/Modal";
import { ErrorModal } from "../../components/Modals/ErrorModal";
import { useSnackBar } from "../../context/SnackbarContext";
import { ChevronDown, Trash2 } from "lucide-react";
import { EmployeeContext } from "../../context/EmployeeContext";
import AddIcon from "@mui/icons-material/Add";
import AddEmailDialog from "../../components/AddEmailDialog";
import { getRolePriority, sortByName } from "../../utils/util";
import { useGetCollaborations } from "../../hooks/collaborationHooks";

type EditCompanyForm = {
    name: string;
    canCreateSubcompany?: boolean;
    emailaddresses?: string[];
    accessMarketplace?: boolean;
    collaboration?: string;
};

export const EditCompanyPage = () => {
    const { companyId, mainCompanyId } = useParams();
    const { data: companyData } = useGetCompany(companyId ?? "");
    const { data: mainCompanyData } = useGetCompany(mainCompanyId ?? "");
    const { isSubSubCompany } = useIsSubSubCompany(companyId ?? "");
    const { data: collaborations } = useGetCollaborations();
    const { employee } = useContext(EmployeeContext);
    const [currentEmployee, setCurrentEmployee] = useState(employee);
    const updateMutation = useUpdateCompany();
    const deleteMutation = useDeleteCompany();
    const navigate = useNavigate();
    const [form, setForm] = useState<EditCompanyForm>({
        name: "",
        canCreateSubcompany: false,
    });
    const [error, setError] = useState<boolean>(false);
    const [errors, setErrors] = useState<ErrorRecord<EditCompanyForm>>({});
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [isAddEmailDialogOpen, setIsAddEmailDialogOpen] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [selected, setSelected] = useState("");

    useEffect(() => {
        if (companyData) {
            setForm({
                name: companyData.name ?? "",
                canCreateSubcompany: companyData.canCreateSubcompany ?? false,
                emailaddresses: companyData.emailaddresses ?? [],
                accessMarketplace: companyData.accessMarketplace ?? false,
                collaboration: companyData.collaboration ?? "",
            });
        }
    }, [companyData]);

    useEffect(() => {
        if (employee) {
            setCurrentEmployee(employee);
        }
    }, [employee]);
    const transformFormData = (form: EditCompanyForm) => ({
        name: form.name,
        canCreateSubcompany: isSubSubCompany ? false : form.canCreateSubcompany,
        emailaddresses: form.emailaddresses,
        accessMarketplace: form.accessMarketplace,
        collaboration: form.collaboration || null,
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(prevForm => ({ ...prevForm, [e.target.name]: e.target.value }));
        setDisableButton(false);
        setError(false);
    };

    const isValidForm = () => {
        let valid = true;
        const newErrors: ErrorRecord<EditCompanyForm> = {};
        if (form.name.length < 1) {
            valid = false;
            newErrors.name = "name cannot be empty";
        }
        if (!valid) {
            setErrors(newErrors);
            setError(true);
            setDisableButton(true);
            return false;
        } else {
            setErrors({});
            setError(false);
            setDisableButton(false);
            return true;
        }
    };

    const snackBar = useSnackBar();

    const handleSubmit = () => {
        if (isValidForm()) {
            if (!companyId) {
                return;
            }
            if (form.accessMarketplace && !form.collaboration) {
                snackBar.showSnackBar("Please select a collaboration sphere", "error");
                return;
            }
            updateMutation.mutate(
                {
                    companyId: companyId,
                    company: transformFormData(form),
                },
                {
                    onError: error => {
                        if (error instanceof Error) {
                            snackBar.showSnackBar(error.message, "error");
                            setDisableButton(true);
                        }
                    },
                    onSuccess: () => {
                        snackBar.showSnackBar("Company updated successfully", "success");
                        navigate(-1);
                    },
                },
            );
        }
    };

    const handleDeleteCompany = () => {
        if (!companyId) {
            return;
        }
        deleteMutation.mutate(
            { companyId },
            {
                onError: error => {
                    if (error instanceof Error) {
                        snackBar.showSnackBar(error.message, "error");
                        setDisableButton(true);
                    }
                },
                onSuccess: () => {
                    snackBar.showSnackBar("Company deleted", "success");
                    navigate(-1);
                },
            },
        );
    };
    const handleChangeEmails = (email: string) => {
        const existingEmailIndex = form.emailaddresses?.findIndex(
            existingEmail => existingEmail === email,
        );
        if (existingEmailIndex !== -1) {
            setForm({
                ...form,
                emailaddresses: form.emailaddresses?.filter(
                    (existingEmail, index) => index !== existingEmailIndex,
                ),
            });
        } else {
            setForm({
                ...form,
                emailaddresses: [...(form.emailaddresses || []), email],
            });
        }
        setDisableButton(false);
        setError(false);
    };
    const handleAddEmailClick = () => {
        setIsAddEmailDialogOpen(true);
    };

    const handleCloseAddEmailDialog = () => {
        setIsAddEmailDialogOpen(false);
        setNewEmail("");
    };

    const handleSaveNewEmail = () => {
        if (newEmail.trim() === "") return;
        setForm(prev => ({
            ...prev,
            emailaddresses: [...(prev.emailaddresses || []), newEmail.trim()],
        }));

        handleCloseAddEmailDialog();
    };
    const { isShown, toggle } = useModal();

    const onConfirm = () => {
        toggle();
        handleDeleteCompany();
    };
    const onCancel = () => toggle();

    return (
        <EditCompanyPageWrapper>
            <EditCompanyWrapper>
                <Modal
                    isShown={isShown}
                    hide={toggle}
                    headerText="Delete the company?"
                    modalContent={
                        <ErrorModal
                            onConfirm={onConfirm}
                            onCancel={onCancel}
                            message="You will not be able to recover it"
                        />
                    }
                />
                <SoecoHeader>Edit Company</SoecoHeader>
                <Form>
                    <TextField
                        name="name"
                        autoComplete="off"
                        error={error && errors.name ? true : false}
                        helperText={error && errors.name}
                        label="Name"
                        variant="outlined"
                        value={form.name}
                        onChange={handleChange}
                    />
                    {employee &&
                        !isSubSubCompany &&
                        (currentEmployee.role === "superAdmin" ||
                            currentEmployee.role === "master") && (
                            <FormControlLabel
                                value="end"
                                control={
                                    <Checkbox
                                        checked={form.canCreateSubcompany}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                canCreateSubcompany: e.target.checked,
                                            }))
                                        }
                                    />
                                }
                                label="Allow subcompany creation"
                                labelPlacement="end"
                            />
                        )}

                    {employee &&
                        currentEmployee.role === "superAdmin" &&
                        !companyData?.superCompany && (
                            <FormControlLabel
                                value="end"
                                control={
                                    <Checkbox
                                        checked={!!form.accessMarketplace}
                                        onChange={e =>
                                            setForm(prev => ({
                                                ...prev,
                                                accessMarketplace: e.target.checked,
                                            }))
                                        }
                                    />
                                }
                                label="Allow access to Internal Marketplace"
                                labelPlacement="end"
                            />
                        )}
                    {form.accessMarketplace && (
                        <>
                            <Typography>Collaboration sphere</Typography>
                            <Select
                                // labelId="filter-company"
                                // label="Acting Company"
                                value={form.collaboration || ""}
                                onChange={event => {
                                    setForm(prev => ({
                                        ...prev,
                                        collaboration: event.target.value,
                                    }));
                                    setSelected(event.target.value);
                                }}
                                IconComponent={ChevronDown}
                                sx={{
                                    fontFamily: "'Yllw Sans a1 241219', sans-serif",
                                    fontSize: "16px",
                                    fontWeight: "400",
                                }}
                            >
                                {sortByName(collaborations ?? []).map(company => (
                                    <MenuItem key={company.id} value={company.id}>
                                        {company.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </>
                    )}

                    {getRolePriority(employee.role) === 0 && (
                        <>
                            <Divider />
                            <Typography>Email Recievers</Typography>
                            {companyData?.emailaddresses?.map((email: string) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            onChange={() => handleChangeEmails(email)}
                                            checked={form.emailaddresses?.includes(email) || false}
                                        />
                                    }
                                    label={email}
                                />
                            ))}
                            {form.emailaddresses?.map((email, index) => {
                                if (!companyData?.emailaddresses?.includes(email)) {
                                    return (
                                        <FormControlLabel
                                            key={index}
                                            control={
                                                <Checkbox
                                                    onChange={() => handleChangeEmails(email)}
                                                    checked={
                                                        form.emailaddresses?.includes(email) ||
                                                        false
                                                    }
                                                />
                                            }
                                            label={email}
                                        />
                                    );
                                }
                            })}
                            <AddEmailDialog
                                isAddEmailDialogOpen={isAddEmailDialogOpen}
                                handleCloseAddEmailDialog={handleCloseAddEmailDialog}
                                newEmail={newEmail}
                                setNewEmail={setNewEmail}
                                handleSaveNewEmail={handleSaveNewEmail}
                            />
                            <IconButton
                                onClick={handleAddEmailClick}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignSelf: "flex-start",
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </>
                    )}
                </Form>
            </EditCompanyWrapper>
            <ActionButtons>
                <SoecoButton
                    color="warning"
                    onClick={toggle}
                    disabled={
                        employee.role !== "superAdmin" &&
                        mainCompanyData?.id === "850d488b-c78c-4db7-9b8e-5b728eebbbe9"
                    }
                >
                    {/* <DeleteIcon fontSize="large" /> */}
                    <Trash2 />
                </SoecoButton>
                <SoecoButton fullWidth size="large" color="warning" onClick={() => navigate(-1)}>
                    Cancel
                </SoecoButton>
                <SoecoButton
                    fullWidth
                    size="large"
                    disabled={updateMutation.isPending || disableButton}
                    color="primary"
                    onClick={handleSubmit}
                >
                    Update
                </SoecoButton>
            </ActionButtons>
        </EditCompanyPageWrapper>
    );
};

const EditCompanyPageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 80vh;
    align-items: center;
    gap: 2rem;
`;

const EditCompanyWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 95%;
    height: 70%;
    align-content: center;
    gap: 2rem;
`;

const Form = styled(FormControl)`
    display: flex;
    flex-direction: column;
    align-content: center;
    width: 95%;
    margin: 0 auto;
    gap: 20px;
`;

const ActionButtons = styled.div`
    display: flex;
    width: 90%;
    gap: 2rem;
    position: fixed;
    bottom: 2rem;
`;
